/**
 * The words a host uses for the things the workbench names, where Paige's word and the desktop's
 * differ. The desktop is CapturePoint, where an operator feeds a scanner all day and the thing they
 * open is a **scan job**; Paige is a cloud service where pages arrive from email, a folder, a
 * scanner or the API, most customers own no scanner, and the word is **job**
 * (docs/engineering/glossary.md). A customer who has never owned a scanner was being told to
 * "Choose a scan job" on the first screen after sign-up.
 *
 * This is the wording sibling of `host-capabilities.ts`: the desktop installs no host and keeps its
 * own words; a host that speaks differently names its words on `window.capturePointHost`, and the
 * renderer reads them through `hostWords()`. Keeping the difference in one place means a CPv6 sync
 * can see every renamed word at once (docs/engineering/workbench-port.md, "What Paige renames").
 */
export interface HostWords {
  /** One taught configuration, mid-sentence: "open a scan job" / "open a job". */
  job: string;
  /** Several, mid-sentence: "search scan jobs" / "search jobs". */
  jobs: string;
  /** One, starting a sentence, a heading or a button: "Scan job" / "Job". */
  jobCapital: string;
  /** Several, starting a sentence, a heading or a button: "Scan jobs" / "Jobs". */
  jobsCapital: string;
  /**
   * The failed setup's banner when the reading ran out of memory (`setup-failure.ts`). The desktop's
   * is about the operator's own computer ("Close other programs"); a host that reads elsewhere says
   * what is true there.
   */
  setupOutOfMemory?: string;
  /**
   * A setup tip (`renderer/setup-tips.ts`, by its id) in the host's words. A tip the host would
   * otherwise hide by its `host` feature shows in these words instead: the host has the thing in its
   * own form (Paige's destinations for the desktop's export, say).
   */
  tips?: Readonly<Record<string, HostTipWords>>;
}

/** What a host says differently in one setup tip; anything it leaves out is the tip's own. */
export interface HostTipWords {
  headline?: string;
  line?: string;
  description?: string;
  chips?: string[];
}

/** CapturePoint's own words, used whenever a host does not say otherwise. */
export const desktopWords: HostWords = {
  job: 'scan job',
  jobs: 'scan jobs',
  jobCapital: 'Scan job',
  jobsCapital: 'Scan jobs',
};

/** The host's words, or the desktop's: outside a window, and on the desktop, nothing is renamed. */
export function hostWords(): HostWords {
  if (typeof window === 'undefined') return desktopWords;
  return window.capturePointHost?.words ?? desktopWords;
}
